<script setup lang="ts">
import GemsCardsItem from '@/components/Gems/GemsCardsItem.vue'
import GWSlider from '@/components/GWSlider/GWSlider.vue'
import { useAdyenActiveCard } from '@gto/api/vue-query'
import { useRouter } from 'vue-router'

import { useStore } from 'vuex'
import { ref, computed, onMounted } from 'vue'

const props = defineProps<{
  credits: {
    credits: number
    price: string
    base_price?: string
    discount_percentage?: string
    recommended?: boolean
  }[]
}>()

const router = useRouter()

const { data: activeCard } = useAdyenActiveCard()

const store = useStore()

function openGemsDialog(item: { credits: number; price: string; offerPercent?: string }) {
  const discount = item.offerPercent ? parseInt(item.offerPercent.replace('%', ''), 10) : undefined
  const numericPrice = parseFloat(item.price.replace(/[^0-9.]/g, ''))

  if (!activeCard.value) {
    router.push({
      name: 'CreditsPayment',
      query: {
        credits: item.credits.toString(),
        price: numericPrice.toString(),
        ...(discount !== undefined ? { discount: discount.toString() } : {}),
      },
    })
    return
  }

  let dialogKey = `gems-dialog_credits-${item.credits}_price-${numericPrice}`
  if (discount !== undefined) {
    dialogKey += `_discount-${discount}`
  }

  store.dispatch('router_set_dialogs', dialogKey)
}

const isMobile = computed(() => store.getters.globals_mobile)

function getImgPath(name: string) {
  return new URL(`../../assets/img/gems/${name}`, import.meta.url).href
}

const items = ref([
  {
    src: getImgPath('chips_more.webp'),
    maxHeight: 260,
  },
  {
    src: getImgPath('chips_stack.webp'),
    maxHeight: 280,
  },
  {
    src: getImgPath('chips_all.webp'),
    maxHeight: 320,
  },
])

const mergedItems = computed(() => {
  if (!props.credits || !Array.isArray(props.credits)) return []

  const sortedCredits = [...props.credits].sort((a, b) => parseFloat(a.price) - parseFloat(b.price))

  // TODO: remove it after getting 3 options from BE
  const lastThreeCredits = sortedCredits.slice(-3)

  const result = lastThreeCredits.map((credit, index, array) => {
    const reverseIndex = items.value.length - (array.length - index)
    const visual = reverseIndex >= 0 ? items.value[reverseIndex] : null

    return {
      credits: credit.credits,
      price: `$${credit.price}`,
      base_price: credit.base_price,
      offerPercent:
        credit.discount_percentage && credit.discount_percentage !== '0'
          ? `${credit.discount_percentage}%`
          : undefined,
      src: visual?.src ?? getImgPath('chips.webp'),
      maxHeight: visual?.maxHeight ?? 240,
      isRecommended: credit?.recommended ?? false,
    }
  })

  return result
})

const recommendedIndex = computed(
  () => mergedItems.value.findIndex(item => item.isRecommended) || 0,
)
</script>
<template>
  <div v-if="isMobile">
    <GWSlider
      class="gems-slider"
      :items="mergedItems"
      peek
      :startIndex="recommendedIndex"
      :showArrows="false"
      showPagination
      :infinite="false"
      style="--height: 327px"
    >
      <template #default="{ item }">
        <GemsCardsItem
          :item="item"
          @click="openGemsDialog(item)"
        />
      </template>
    </GWSlider>
  </div>
  <div
    v-else
    class="gems-cards"
  >
    <GemsCardsItem
      v-for="item in mergedItems"
      :key="item.price"
      :item="item"
      @click="openGemsDialog(item)"
    />
  </div>
</template>

<style lang="scss" scoped>
.gems-slider {
  background: var(--palette-background-paper);
}

.gems-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  row-gap: 6rem;
  background: var(--palette-background-paper);
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
  padding: 1px;
  box-sizing: border-box;
  justify-content: center;
}

:deep(.slideshow-circle) {
  background: #a8a8b161;
}

:deep(.card.card--active) {
  width: 70%;
}

:deep(.slideshow-circle_active) {
  background: var(--palette-primary-main);
}

:deep(.slideshow-circles) {
  bottom: 1rem;
}
</style>
